import { BACKUP_VERSION } from '../app/version';
import { base64ToBlob, blobToBase64, createId, isoNow, sha256Blob, sha256Text } from './crypto';
import { normalizeStudyRecord, assertPlainObject, assertSafeCollectionSize } from './schema';
import type { IndexedDbStudyRepository } from './repository';
import {
  CURRENT_SCHEMA_VERSION,
  type AttachmentMetadata,
  type BackupManifest,
  type DatabaseDump,
  type JsonBackupEnvelope,
  type OutboxEntry,
  type PortableAttachment,
  type PortableDump,
  type RestoreMode,
  type RestoreResult,
  type SettingRecord,
  type StoredAttachment,
} from './types';

export const BACKUP_FORMAT = 'nse-study-bundle' as const;
export const MAX_JSON_BACKUP_BYTES = 250 * 1024 * 1024;

const MAX_RECORDS = 50_000;
const MAX_ATTACHMENTS = 5_000;
const MAX_SETTINGS = 2_000;
const MAX_OUTBOX = 50_000;
const SECRET_SETTING_PATTERN = /(token|secret|password|authorization|api[-_]?key|session)/i;

export interface ExportBackupOptions {
  appVersion?: string;
  contentVersion?: string;
  includeAttachments?: boolean;
  includeOutbox?: boolean;
}

export interface RestoreBackupOptions {
  mode?: RestoreMode;
  createSafetySnapshot?: boolean;
}

function publicSettings(settings: SettingRecord[]): SettingRecord[] {
  return settings.filter((setting) =>
    !SECRET_SETTING_PATTERN.test(setting.key)
    && setting.key !== 'system:device-id'
    && !setting.key.startsWith('sync:')
    && !setting.key.startsWith('migration:legacy-raw:'),
  );
}

function attachmentMetadata(attachment: StoredAttachment): AttachmentMetadata {
  const { blob: _blob, ...metadata } = attachment;
  return metadata;
}

async function buildPortableDump(
  repository: IndexedDbStudyRepository,
  options: ExportBackupOptions,
): Promise<PortableDump> {
  const dump = await repository.dump();
  const attachments: PortableAttachment[] = [];
  if (options.includeAttachments !== false) {
    for (const attachment of dump.attachments) {
      attachments.push({
        ...attachmentMetadata(attachment),
        dataBase64: await blobToBase64(attachment.blob),
      });
    }
  }
  return {
    records: dump.records,
    attachments,
    settings: publicSettings(dump.settings),
    outbox: options.includeOutbox === false ? [] : dump.outbox,
  };
}

export async function createJsonBackup(
  repository: IndexedDbStudyRepository,
  options: ExportBackupOptions = {},
): Promise<JsonBackupEnvelope> {
  const deviceId = await repository.getDeviceId();
  const data = await buildPortableDump(repository, options);
  const dataHash = await sha256Text(JSON.stringify(data));
  const manifest: BackupManifest = {
    format: BACKUP_FORMAT,
    schemaVersion: CURRENT_SCHEMA_VERSION,
    appVersion: options.appVersion ?? BACKUP_VERSION.appVersion,
    exportId: createId('export'),
    exportedAt: isoNow(),
    deviceId,
    contentVersion: options.contentVersion ?? BACKUP_VERSION.contentVersion,
    kind: 'json',
    counts: {
      records: data.records.length,
      attachments: data.attachments.length,
      settings: data.settings.length,
      outbox: data.outbox.length,
    },
    integrity: { algorithm: 'SHA-256', dataHash },
  };
  return { manifest, data };
}

export async function createJsonBackupBlob(
  repository: IndexedDbStudyRepository,
  options: ExportBackupOptions = {},
): Promise<Blob> {
  const envelope = await createJsonBackup(repository, options);
  const blob = new Blob([JSON.stringify(envelope, null, 2)], { type: 'application/json' });
  if (blob.size > MAX_JSON_BACKUP_BYTES) {
    throw new Error(`完整备份超过 ${(MAX_JSON_BACKUP_BYTES / 1024 / 1024).toFixed(0)} MB；请先清理无用附件`);
  }
  return blob;
}

export function downloadJsonBackup(blob: Blob, exportedAt = isoNow()): void {
  if (typeof document === 'undefined') throw new Error('当前环境不支持浏览器下载');
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `核安全备考-完整备份-${exportedAt.replace(/[:.]/g, '-')}.json`;
  link.style.display = 'none';
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function parseEnvelope(value: unknown): JsonBackupEnvelope {
  assertPlainObject(value, '备份文件');
  assertPlainObject(value.manifest, '备份清单');
  assertPlainObject(value.data, '备份数据');
  if (value.manifest.format !== BACKUP_FORMAT) throw new Error('不是核安全学习中心备份文件');
  const version = Number(value.manifest.schemaVersion);
  if (!Number.isInteger(version) || version < 1 || version > CURRENT_SCHEMA_VERSION) {
    throw new Error(`不支持的备份版本：${version}`);
  }
  const data = value.data as unknown as PortableDump;
  if (!Array.isArray(data.records) || !Array.isArray(data.attachments)
    || !Array.isArray(data.settings) || !Array.isArray(data.outbox)) {
    throw new Error('备份数据集合不完整');
  }
  assertSafeCollectionSize('记录', data.records.length, MAX_RECORDS);
  assertSafeCollectionSize('附件', data.attachments.length, MAX_ATTACHMENTS);
  assertSafeCollectionSize('设置', data.settings.length, MAX_SETTINGS);
  assertSafeCollectionSize('同步队列', data.outbox.length, MAX_OUTBOX);
  assertPlainObject(value.manifest.integrity, '完整性信息');
  if (value.manifest.integrity.algorithm !== 'SHA-256'
    || typeof value.manifest.integrity.dataHash !== 'string') {
    throw new Error('备份缺少 SHA-256 完整性信息');
  }
  assertPlainObject(value.manifest.counts, '备份数量清单');
  const counts = value.manifest.counts;
  if (counts.records !== data.records.length || counts.attachments !== data.attachments.length
    || counts.settings !== data.settings.length || counts.outbox !== data.outbox.length) {
    throw new Error('备份清单数量与实际数据不一致');
  }
  return value as unknown as JsonBackupEnvelope;
}

function normalizeSettings(values: SettingRecord[]): SettingRecord[] {
  return values
    .filter((value) => value && typeof value.key === 'string' && !SECRET_SETTING_PATTERN.test(value.key))
    .map((value) => ({
      key: value.key,
      value: value.value,
      updatedAt: Number.isFinite(Date.parse(value.updatedAt)) ? value.updatedAt : isoNow(),
    }));
}

function normalizeOutbox(values: OutboxEntry[]): OutboxEntry[] {
  return values.filter((value) => value
    && typeof value.id === 'string'
    && typeof value.entityKey === 'string'
    && typeof value.operation === 'string');
}

async function portableToDump(
  portable: PortableDump,
  fallbackDeviceId: string,
): Promise<{ dump: DatabaseDump; warnings: string[] }> {
  const warnings: string[] = [];
  const records = portable.records.map((record) => normalizeStudyRecord(record, fallbackDeviceId));
  const attachments: StoredAttachment[] = [];
  for (const value of portable.attachments) {
    if (!value || typeof value.id !== 'string' || typeof value.dataBase64 !== 'string') {
      warnings.push(`跳过缺少二进制内容的附件：${String(value?.id ?? '未知')}`);
      continue;
    }
    const blob = base64ToBlob(value.dataBase64, value.mimeType || 'application/octet-stream');
    if (blob.size !== Number(value.byteSize)) throw new Error(`附件 ${value.id} 大小校验失败`);
    if (await sha256Blob(blob) !== value.sha256) throw new Error(`附件 ${value.id} 哈希校验失败`);
    const { dataBase64: _data, path: _path, ...metadata } = value;
    attachments.push({ ...metadata, blob } as StoredAttachment);
  }
  return {
    dump: {
      records,
      attachments,
      settings: normalizeSettings(portable.settings),
      outbox: normalizeOutbox(portable.outbox),
    },
    warnings,
  };
}

function newer<T extends { updatedAt: string; revision?: number }>(incoming: T, current?: T): boolean {
  if (!current) return true;
  const revisionDiff = Number(incoming.revision ?? 0) - Number(current.revision ?? 0);
  return revisionDiff > 0 || (revisionDiff === 0 && incoming.updatedAt > current.updatedAt);
}

async function mergeDump(repository: IndexedDbStudyRepository, incoming: DatabaseDump): Promise<DatabaseDump> {
  const current = await repository.dump();
  const records = new Map(current.records.map((record) => [record.key, record]));
  incoming.records.forEach((record) => {
    if (newer(record, records.get(record.key))) records.set(record.key, record);
  });
  const attachments = new Map(current.attachments.map((attachment) => [attachment.id, attachment]));
  incoming.attachments.forEach((attachment) => {
    if (newer(attachment, attachments.get(attachment.id))) attachments.set(attachment.id, attachment);
  });
  const settings = new Map(current.settings.map((setting) => [setting.key, setting]));
  incoming.settings.forEach((setting) => {
    if (newer(setting, settings.get(setting.key))) settings.set(setting.key, setting);
  });
  const outbox = new Map(current.outbox.map((entry) => [entry.id, entry]));
  incoming.outbox.forEach((entry) => outbox.set(entry.id, entry));
  return {
    records: [...records.values()],
    attachments: [...attachments.values()],
    settings: [...settings.values()],
    outbox: [...outbox.values()],
  };
}

export async function restoreJsonBackup(
  repository: IndexedDbStudyRepository,
  input: Blob | string | JsonBackupEnvelope,
  options: RestoreBackupOptions = {},
): Promise<RestoreResult> {
  if (input instanceof Blob && input.size > MAX_JSON_BACKUP_BYTES) {
    throw new Error(`备份文件超过 ${(MAX_JSON_BACKUP_BYTES / 1024 / 1024).toFixed(0)} MB 安全上限`);
  }
  const raw = input instanceof Blob ? await input.text() : typeof input === 'string' ? input : undefined;
  if (raw && new TextEncoder().encode(raw).byteLength > MAX_JSON_BACKUP_BYTES) {
    throw new Error('备份文本超过安全上限');
  }
  let parsed: unknown;
  try {
    parsed = raw === undefined ? input : JSON.parse(raw);
  } catch {
    throw new Error('备份不是有效 JSON');
  }
  const envelope = parseEnvelope(parsed);
  const actualHash = await sha256Text(JSON.stringify(envelope.data));
  if (actualHash !== envelope.manifest.integrity.dataHash) throw new Error('备份数据哈希不一致，文件可能损坏');

  if (options.createSafetySnapshot !== false) {
    const safetyBlob = await createJsonBackupBlob(repository, { includeAttachments: true, includeOutbox: true });
    await repository.saveSnapshot({
      id: createId('snapshot'),
      reason: 'before-import',
      createdAt: isoNow(),
      schemaVersion: CURRENT_SCHEMA_VERSION,
      blob: safetyBlob,
    });
  }

  const fallbackDeviceId = await repository.getDeviceId();
  const converted = await portableToDump(envelope.data, fallbackDeviceId);
  const mode = options.mode ?? 'merge';
  const dump = mode === 'merge' ? await mergeDump(repository, converted.dump) : converted.dump;
  await repository.restoreDump(dump, 'replace');
  await repository.pruneSnapshots();

  return {
    mode,
    records: converted.dump.records.length,
    attachments: converted.dump.attachments.length,
    settings: converted.dump.settings.length,
    outbox: converted.dump.outbox.length,
    warnings: converted.warnings,
  };
}
